import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import {
  Button,
  Card,
  CardClose,
  CardHeader,
  Command,
  CommandGroup,
  CommandItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ais-ui/ui-core';
import { capitalize, cn } from '@ais-ui/utils';
import { usePagination, useTargetsOnMap } from '../TargetsOnMapContext';
import { useLocalization } from '@ais-ui/locale-management';
import { useMapPanelContext } from '../MapPanel/MapPanelContext';
import {
  PiArrowSquareOut,
  PiBoat,
  PiCaretDown,
  PiCaretUp,
  PiCaretUpDown,
  PiDotsThreeVerticalBold,
  PiFileCsv,
} from 'react-icons/pi';
import { useAISMapContext } from '../AISMapContext';
import { AutoCompleteShipInfo, TargetFromTargetList } from '@ais-ui/websockets';
import { CENTERCOMPONENTS } from '../MapPanel/CenterComponents';
import { useTableDensity } from '../../utils/useTableDensity';
import {
  getDimensionsFromMap,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { openPopupOnSecondaryScreen, PopupType } from '../../Popup';
import { ToString } from '@formatjs/ecma402-abstract';

interface TargetsOnMapTableProps {
  className?: string;
}

interface SortableTableHeadProps {
  children: React.ReactNode;
  column: string;
  density: 'normal' | 'compact';
  densityClasses: any;
}

const SortableTableHead = ({
  children,
  column,
  density,
  densityClasses,
}: SortableTableHeadProps) => {
  const {
    orderBy,
    setOrderBy,
    orderAscending,
    setOrderAscending,
    onGetTargets,
  } = useTargetsOnMap();
  const [sortAction, setSortAction] = useState(false);

  const isSorted = orderBy === column;

  const handleSort = useCallback(() => {
    if (orderBy === column) {
      setOrderAscending(!orderAscending);
    } else {
      setOrderAscending(true);
    }
    setOrderBy(column);

    setSortAction(true);
  }, [orderBy, column, setOrderAscending, orderAscending, setOrderBy]);

  useEffect(() => {
    if (sortAction) {
      onGetTargets();
      setSortAction(false);
    }
  }, [sortAction, onGetTargets]);

  return (
    <TableHead
      className={cn('cursor-pointer select-none', densityClasses.tableHeader)}
      onClick={handleSort}
    >
      <div className="flex items-center justify-between">
        <span className={densityClasses.text}>{children}</span>
        <span className="mr-2">
          {isSorted ? (
            orderAscending ? (
              <PiCaretUp className={densityClasses.icon.small} />
            ) : (
              <PiCaretDown className={densityClasses.icon.small} />
            )
          ) : (
            <PiCaretUpDown
              className={cn(densityClasses.icon.small, 'opacity-50')}
            />
          )}
        </span>
      </div>
    </TableHead>
  );
};

const TargetsOnMapActionDropDown = () => {
  const { onCreateShiplist, isSubmitting, exportTargetsToCSV } =
    useTargetsOnMap();
  const { tableDensity, densityClasses } = useTableDensity();
  const [shiplistName, setShiplistName] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { messages } = useLocalization();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'm-0',
            tableDensity === 'compact' && 'h-8 px-3 text-xs',
          )}
        >
          Actions{' '}
          <ChevronDownIcon className={cn('ml-2', densityClasses.icon.small)} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <Command>
          <CommandGroup heading="Actions">
            <CommandItem onSelect={exportTargetsToCSV} disabled={isSubmitting}>
              <PiFileCsv className={cn('mr-2', densityClasses.icon.small)} />
              {messages.targetsOnMapExportCsvText}
            </CommandItem>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <CommandItem onSelect={() => setIsPopoverOpen(true)}>
                  <PiBoat className={cn('mr-2', densityClasses.icon.small)} />
                  {messages.targetsOnMapCreateShiplist}
                </CommandItem>
              </PopoverTrigger>
              <PopoverContent side={'right'}>
                <Input
                  placeholder="name"
                  value={shiplistName}
                  onChange={(e) => setShiplistName(e.target.value)}
                  className={cn(tableDensity === 'compact' && 'h-8 text-xs')}
                ></Input>
                <div className="w-full flex justify-end">
                  <Button
                    onClick={() => onCreateShiplist(shiplistName)}
                    className={cn(
                      'mt-2',
                      tableDensity === 'compact' && 'h-8 px-3 text-xs',
                    )}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? messages.genericSubmitting
                      : messages.genericCreate}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const TargetsOnMapTable = memo(
  ({ className }: TargetsOnMapTableProps) => {
    const { tableExpanded, setTableExpanded, tableHeight, setCenterComponent } =
      useMapPanelContext();
    const { messages } = useLocalization();
    const { tableDensity, densityClasses, TruncatedCell, ShortTextCell } = useTableDensity();

    const viewportRef = useRef<HTMLDivElement | null>(null);

    const { setZoomToShip } = useAISMapContext();
    const { map, displayProjection, dataProjection } = useOpenLayersContext();

    const { onGetTargets, targets, page, setPage, perPage, total, isLoading, retrievalTime } =
      useTargetsOnMap();
    const pagination = usePagination(page, total, perPage);

    const onPrevious = useCallback(() => {
      setPage((prev) => Math.max(prev - 1, 1));
    }, [setPage]);

    const onNext = useCallback(() => {
      setPage((prev) => Math.min(prev + 1, Math.ceil(total / perPage)));
    }, [setPage, total, perPage]);

    useEffect(() => {
      onGetTargets();
    }, [page, onGetTargets]);

    const onGoToTarget = useCallback(
      (target: TargetFromTargetList) => {
        // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
        const name = target.name !== target.mmsi ? target.name : '';
        const autoCompleteShip: AutoCompleteShipInfo = {
          name: name,
          mmsi: target.mmsi !== null && target.mmsi !== undefined ? ToString(target.mmsi) : null,
          imo: target.imo !== null && target.imo !== undefined ? ToString(target.imo) : null,
          callSign: target.callSign,
          type: 'TT_SHIP',
        };
        setZoomToShip(autoCompleteShip);
      },
      [setZoomToShip],
    );

    const onClose = useCallback(() => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }, [setCenterComponent]);

    const handleOpenInPopup = useCallback(() => {
      const dimensions = getDimensionsFromMap(map, displayProjection, dataProjection);
      if (!dimensions) return;

      const { northEast, southWest } = dimensions;

      openPopupOnSecondaryScreen(PopupType.Targets, {
        minLat: southWest.lat,
        maxLat: northEast.lat,
        minLon: southWest.lng,
        maxLon: northEast.lng,
      });
    }, [map, displayProjection, dataProjection]);

    if (!tableExpanded) {
      return null;
    }

    return (
      <Card
        className={cn(
          'relative flex flex-col h-fit w-full transition-all group/data-table pl-1 pr-3 pb-4 pt-1',
          className,
        )}
      >
        <CardHeader
          className={cn(
            'text-lg font-normal text-muted-foreground',
            densityClasses.cardHeader,
          )}
        >
          <div className="flex justify-between items-center">
            <div
              className={cn(
                'text-muted-foreground pl-1',
                densityClasses.text,
              )}
            >
              {total} rows
            </div>
            <div className="flex items-center gap-4">
              <TargetsOnMapActionDropDown />
              <div className={densityClasses.text}>
                {page} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={onPrevious}
                  disabled={page === 1}
                  className={cn(
                    tableDensity === 'compact' && 'h-8 px-3 text-xs',
                  )}
                >
                  {messages.genericPaginationPrevious}
                </Button>
                <Button
                  variant="outline"
                  onClick={onNext}
                  disabled={page >= pagination.totalPages}
                  className={cn(
                    tableDensity === 'compact' && 'h-8 px-3 text-xs',
                  )}
                >
                  {messages.genericPaginationNext}
                </Button>
              </div>
              <button
                id="open-targets-popup-button"
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={handleOpenInPopup}
                title="Open in new window"
              >
                <PiArrowSquareOut className={densityClasses.icon.medium} />
              </button>
              <CardClose onClick={onClose} className="static" />
            </div>
          </div>
        </CardHeader>
        <div className="flex overflow-hidden">
          <ScrollArea
            infiniteScrollRef={viewportRef}
            observeResize={false}
            className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
          >
            <Table container={false}>
              <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
                <TableRow className="hover:bg-transparent">
                  <TableHead className={cn('w-4', densityClasses.tableHeader)}>
                    <span className={densityClasses.text}>#</span>
                  </TableHead>
                  <SortableTableHead
                    column="name"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderName}
                  </SortableTableHead>
                  <SortableTableHead
                    column="mmsi"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderMmsi}
                  </SortableTableHead>
                  <SortableTableHead
                    column="imo"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderImo}
                  </SortableTableHead>
                  <SortableTableHead
                    column="callsign"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCallsign}
                  </SortableTableHead>
                  <SortableTableHead
                    column="shiptype"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderShipType}
                  </SortableTableHead>
                  <SortableTableHead
                    column="type"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderAisType}
                  </SortableTableHead>
                  <SortableTableHead
                    column="country"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCountry}
                  </SortableTableHead>
                  <TableHead className={densityClasses.tableHeader}>
                    <span className={densityClasses.text}>
                      {messages.targetsOnMapHeaderPosition}
                    </span>
                  </TableHead>
                  <SortableTableHead
                    column="sog"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderSpeed}
                  </SortableTableHead>
                  <SortableTableHead
                    column="cog"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCourse}
                  </SortableTableHead>
                  <SortableTableHead
                    column="heading"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderHeading}
                  </SortableTableHead>
                  <SortableTableHead
                    column="rot"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderRateOfTurn}
                  </SortableTableHead>
                  <SortableTableHead
                    column="width"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderWidth}
                  </SortableTableHead>
                  <SortableTableHead
                    column="length"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderLength}
                  </SortableTableHead>
                  <SortableTableHead
                    column="draught"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderDraught}
                  </SortableTableHead>
                  <TableHead className={densityClasses.tableHeader}></TableHead>
                </TableRow>
              </TableHeader>
              {isLoading && (
                <TableBody>
                  <TableRow className="border-0">
                    <TableCell colSpan={17}>
                      <div
                        className="flex justify-center items-center"
                        style={{ height: `${tableHeight - 170}px` }}
                      >
                        <LoadingSpinner />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
              {!isLoading && (
                <TableBody>
                  {targets.map((target, index) => (
                    <TableRow
                      key={`${target.mmsi}-${target.name}`}
                      className="odd:bg-muted/50"
                    >
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {(page - 1) * perPage + index + 1}
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn('font-medium', densityClasses.tableCell)}
                      >
                        <TruncatedCell
                          text={capitalize(target.name)}
                          className={cn(densityClasses.cellContent, densityClasses.truncation.medium)}
                        >
                          {capitalize(target.name)}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {target.mmsi}
                        </div>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {target.imo}
                        </div>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.callSign}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={target.shipType || ''}
                          className={cn(densityClasses.cellContent, densityClasses.truncation.medium)}
                        >
                          {target.shipType}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.type}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={target.country || ''}
                          className={cn(densityClasses.cellContent, densityClasses.truncation.medium)}
                        >
                          {target.country}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={`${target.lat.toFixed(4)}, ${target.lon.toFixed(
                            4,
                          )}`}
                          className={cn(
                            densityClasses.cellContent,
                            tableDensity === 'compact' ? 'max-w-32' : 'max-w-40'
                          )}
                        >
                          {`${target.lat.toFixed(4)}, ${target.lon.toFixed(4)}`}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.sog && `${target.sog} knots`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.cog && `${target.cog} °`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.heading && `${target.heading} °`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.rot && `${target.rot} °/min`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.width && `${target.width} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.length && `${target.length} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.draught && `${target.draught} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className={densityClasses.button}
                              >
                                <PiDotsThreeVerticalBold
                                  className={densityClasses.icon.medium}
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => onGoToTarget(target)}
                              >
                                View target
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </Card>
    );
  },
);

TargetsOnMapTable.displayName = 'TargetsOnMapTable';
